import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { IdentityProvider } from '@senviro/core-api-client';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';

/**
 * `useIdentityProvider` (W5-F3-S1) — fetch the active per-partner Entra
 * IdP for the caller's org. Returns `undefined` while loading, `null` when
 * no row is configured (or all rows are inactive), or the
 * `IdentityProvider` when one is active.
 *
 * Cache key: `['idp', tid]` with 10-min stale time. IdP config rarely
 * changes; aggressive caching avoids hitting Core on every page nav.
 */
export function useIdentityProvider(): UseQueryResult<
  IdentityProvider | null
> {
  const { account, getAccessToken } = useAuth();
  const tid = resolveTid(account);

  return useQuery<IdentityProvider | null>({
    queryKey: ['idp', tid] as const,
    enabled: tid != null,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      // Backend wraps the response in `{ data }`. Mirror the
      // null-on-empty unwrap from W5-F1-S1 so the consumer surface is
      // always `IdentityProvider | null`.
      const raw = await apiFetch<
        IdentityProvider | { data: null } | null
      >('/api/v1/proxy/core/tenant-identity-providers', {
        getAccessToken,
      });
      if (
        raw &&
        typeof raw === 'object' &&
        'data' in raw &&
        (raw as { data: unknown }).data === null
      ) {
        return null;
      }
      return raw as IdentityProvider | null;
    },
  });
}

function resolveTid(
  account: ReturnType<typeof useAuth>['account'],
): string | null {
  if (!account) return null;
  const maybe = (account as { tenantId?: unknown }).tenantId;
  return typeof maybe === 'string' && maybe.length > 0 ? maybe : null;
}
